import { Tooltip } from '@material-ui/core';
import { DatePicker, LocalizationProvider, TimePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { isAfter, isEqual, isValid, set, startOfDay } from 'date-fns';
import React, { forwardRef, useCallback, useEffect, useImperativeHandle } from 'react';
import { useForm, Controller, useWatch } from 'react-hook-form';
import { TaskCreationPayloadType, TaskTypesEnum } from '@redux/tasks/tasksTypes';
import { BestDeliveryTime } from 'components/CaregiverAssistantDrawer/declarations/enums';
import { ExpirySelectBlock } from 'components/ExpirySelectBlock/ExpirySelectBlock';
import { ENDS_SELECT_OPTIONS, FREQUENCY_SELECT_OPTIONS, FrequencySelectBlock, FrequencyType } from 'components/FrequencySelectBlock/FrequencySelectBlock';
import { WaveformIcon } from 'components/Icons';
import { Switcher } from 'components/Switcher/Switcher';
import { TextArea } from 'components/TextArea/TextArea';
import { BedroomTriggerTypes, LivingSpaceTriggerTypes, TriggerSelectBlock, TriggerTypes } from 'components/TriggerSelectBlock/TriggerSelectBlock';
import { Text } from 'components/Typography';
import { customDateTimePickerStyles } from '../../../../customStyles';
import style from './ScheduleForm.scss';
import { CaregiverCategoryProgram } from '@redux/caregiverAssistant/caregiverAssistantTypes';
import { DeviceModeState, Person } from '@redux/person/personTypes';

type ScheduleFormValues = {
  promptText: string;
  date: Date | null;
  deliverAt: Date | null;
  expiredTime: Date | null;
  trigger: TriggerTypes;
  frequency: FrequencyType;
  responseRequired: boolean;
};

type Props = {
  prompt: CaregiverCategoryProgram;
  onSubmit: (values: TaskCreationPayloadType) => void;
  person?: Nullable<Person>;
  deviceMode?: DeviceModeState;
  onValidityChange?: (isValid: boolean) => void;
};

export type SchedulePromptFormRef = {
  submitScheduleForm: () => void;
};

const SCHEDULED_IN_FUTURE_ERROR = 'Scheduled date/time must be in the future';

const isDateInFutureOrToday = (date: Date, now: Date) => {
  const selectedDay = startOfDay(date);
  const today = startOfDay(now);
  return isAfter(selectedDay, today) || isEqual(selectedDay, today);
};

const isDateTimeInFuture = (date: Date, time: Date, now: Date) => {
  const scheduled = set(date, {
    hours: time.getHours(),
    minutes: time.getMinutes(),
    seconds: 0,
    milliseconds: 0
  });
  return isAfter(scheduled, now);
};

const getDefaultTimeForBestDelivery = (bestDeliveryTime?: BestDeliveryTime | null): Date | null => {
  const now = new Date();
  const date = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  switch (bestDeliveryTime) {
    case BestDeliveryTime.Morning:
      return new Date(date.setHours(9, 0, 0, 0));
    case BestDeliveryTime.Afternoon:
      return new Date(date.setHours(13, 0, 0, 0));
    case BestDeliveryTime.Evening:
      return new Date(date.setHours(19, 0, 0, 0));
    // Field left blank for prompts that we do not want a suggested value
    // https://cairns.atlassian.net/browse/SW-3094?focusedCommentId=30603
    case BestDeliveryTime.ANY:
    default:
      return null;
  }
};

const isValidTimeValue = (value: Date | null): value is Date => {
  return value != null && isValid(value);
};

export const SchedulePromptForm = forwardRef<SchedulePromptFormRef, Props>(({ prompt, person, deviceMode, onSubmit, onValidityChange }, ref) => {
  const { control, handleSubmit, reset, formState, getValues, trigger } = useForm<ScheduleFormValues>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      promptText: 'Remember to drink water today.',
      date: new Date(),
      deliverAt: null,
      trigger: TriggerTypes.enteringBed,
      frequency: {
        repeat: FREQUENCY_SELECT_OPTIONS.no_repeat,
        ends: ENDS_SELECT_OPTIONS.after,
        after: 1,
        date: new Date()
      },
      expiredTime: null,
      responseRequired: false
    }
  });

  const deliverAt = useWatch({ control, name: 'deliverAt' });
  const scheduledDate = useWatch({ control, name: 'date' });

  const validateScheduledDate = useCallback((value?: Date | null) => {
    if (!value) return true;
    return isDateInFutureOrToday(value, new Date()) || SCHEDULED_IN_FUTURE_ERROR;
  }, []);

  const validateScheduledTime = useCallback(
    (value?: Date | null) => {
      if (!value) return true;
      const selectedDate = getValues('date');
      if (!selectedDate) return true;
      return isDateTimeInFuture(selectedDate, value, new Date()) || SCHEDULED_IN_FUTURE_ERROR;
    },
    [getValues]
  );

  useEffect(() => {
    if (prompt) {
      reset({
        promptText: prompt.description,
        date: new Date(),
        deliverAt: getDefaultTimeForBestDelivery(prompt.bestDeliveryTime),
        trigger: deviceMode === DeviceModeState.LIVING_SPACE ? LivingSpaceTriggerTypes[0] : BedroomTriggerTypes[0],
        frequency: {
          repeat: FREQUENCY_SELECT_OPTIONS.no_repeat,
          ends: ENDS_SELECT_OPTIONS.after,
          after: 1,
          date: new Date()
        },
        expiredTime: null,
        responseRequired: prompt.insightRequired ?? false
      });
    }
  }, [prompt, reset]);

  useEffect(() => {
    onValidityChange?.(formState.isValid);
  }, [formState.isValid, onValidityChange]);

  useEffect(() => {
    if (!deliverAt) return;
    trigger('deliverAt');
  }, [scheduledDate, deliverAt, trigger]);

  const buildDateTime = (date: Date | null, time: Date | null): string => {
    if (!date || !time) {
      return new Date().toISOString();
    }

    const d = new Date(date.getFullYear(), date.getMonth(), date.getDate(), time.getHours(), time.getMinutes(), 0, 0);

    return d.toISOString();
  };

  const mapToTaskCreationPayload = (values: ScheduleFormValues): TaskCreationPayloadType => {
    const scheduledForDateTime = buildDateTime(values.date, values.deliverAt);
    const expiredForDateTime = values.expiredTime != null ? values.expiredTime.toISOString() : scheduledForDateTime;
    return {
      taskMessage: values.promptText,
      assigneeId: person?.id!,
      assigneeFullName: person?.fullName!,
      scheduledForDateTime,
      generatedMessage: values.promptText,
      taskName: prompt.name,
      taskType: prompt.programType,
      assessmentId: prompt.id,
      frequency: values.frequency.repeat,
      frequencyEnds: (values.frequency.ends ?? null) as Nullable<typeof values.frequency.ends>,
      endsOnDate: values.frequency.date ? values.frequency.date.toISOString() : null,
      endsAfterOccurrences: (values.frequency.after ?? null) as Nullable<number>,
      triggerCondition: values.trigger,
      responseRequired: values.responseRequired,
      expiredForDateTime,
      taskSummary: undefined
    };
  };

  // useImperativeHandle lets a child component expose specific functions to its parent through a ref
  useImperativeHandle(ref, () => ({
    submitScheduleForm: async () => {
      const validatedFn = handleSubmit( async (values) => {
        if (!person) return;
        const payload = mapToTaskCreationPayload(values);
        onSubmit(payload);
      });
      await validatedFn();
    }
  }));

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <div className={style.messageCard}>
        <div className={style.iconBox}>
          <WaveformIcon />
        </div>
        <Tooltip title="An example message is shown. Your actual task message will be generated at the time it’s due, using your real-time profile data." arrow>
          <span className={style.messageTitle}>
            <Text>Example Message</Text>
          </span>
        </Tooltip>
        <Controller
          name="promptText"
          control={control}
          render={({ field, fieldState }) => <TextArea disabled rows={1} isTransparent {...field} error={!!fieldState.error} helperText={fieldState.error?.message} />}
        />
      </div>
      <div className={style.formSection}>
        <div className={style.fieldSection}>
          <Text className={style.sectionTitle}>Date</Text>
          <Controller
            name="date"
            control={control}
            rules={{
              required: SCHEDULED_IN_FUTURE_ERROR,
              validate: validateScheduledDate
            }}
            render={({ field, fieldState }) => (
              <DatePicker
                {...field}
                format="MMMM d, yyyy"
                disablePast
                sx={customDateTimePickerStyles}
                slotProps={{
                  textField: {
                    error: !!fieldState.error,
                    helperText: fieldState.error?.message
                  }
                }}
              />
            )}
          />
        </div>
      </div>
      <div className={style.formSection}>
        <div className={style.fieldSection}>
          <Text className={style.sectionTitle}>Deliver at</Text>
          <Controller
            name="deliverAt"
            control={control}
            rules={{
              required: SCHEDULED_IN_FUTURE_ERROR,
              validate: validateScheduledTime
            }}
            render={({ field, fieldState }) => (
              <TimePicker
                {...field}
                value={field.value}
                onChange={(value) => {
                  if (value === null) {
                    field.onChange(null);
                    return;
                  }

                  if (!isValidTimeValue(value)) {
                    return;
                  }

                  field.onChange(value);
                }}
                sx={customDateTimePickerStyles}
                slotProps={{
                  textField: {
                    error: !!fieldState.error,
                    helperText: fieldState.error?.message
                  }
                }}
              />
            )}
          />
        </div>
        <div className={style.fieldSection}>
          <Controller
            name="expiredTime"
            control={control}
            render={({ field }) => (
              <ExpirySelectBlock selectedTime={deliverAt || new Date()} expiredTime={field.value} onExpiredTimeSelected={field.onChange} disabled={!deliverAt} />
            )}
          />
        </div>
      </div>
      <div className={style.formSection}>
        <div className={`${style.fieldSection} ${style.fieldSectionFull}`}>
          <Controller
            name="trigger"
            control={control}
            render={({ field, fieldState }) => <TriggerSelectBlock trigger={field.value as any} onTriggerChanged={field.onChange} deviceMode={deviceMode} error={fieldState.error?.message} />}
          />
        </div>
      </div>
      <div className={style.formSection}>
        <div className={`${style.fieldSection} ${style.fieldSectionFull}`}>
          <Controller
            name="frequency"
            control={control}
            render={({ field }) => (
              <FrequencySelectBlock
                frequency={field.value}
                errors={{ after: undefined, date: undefined }}
                onFrequencyChanged={(valueOrUpdater) => field.onChange(typeof valueOrUpdater === 'function' ? valueOrUpdater(field.value) : valueOrUpdater)}
              />
            )}
          />
        </div>
      </div>
      <div className={style.responseRequiredBox}>
        <div className={style.switchSection}>
          <Text>Response required?</Text>
          <Controller name="responseRequired" control={control} render={({ field }) => <Switcher {...field} checked={field.value} />} />
        </div>
        <Text className={style.responseDescription}>If yes it will be included in the weekly report.</Text>
      </div>
    </LocalizationProvider>
  );
});
